import {action, makeObservable, observable, runInAction} from "mobx";
import {IEventCatalogueService} from "../../../services/eventCatalogueService";
import {IEventService} from "../../../services/eventService";
import {FileCreated, IFileService} from "../../../services/fileService";
import {ResourceCreatedOrUpdated} from "../../../services/resourceCreatedOrUpdated";
import {EventTemplateStatus} from "../../../shared/enums/eventTemplateStatus";
import {KeyValue} from "../../observables/keyValue";
import EventTemplate from "./models/eventTemplate";
import EventTemplateCategory from "./models/eventTemplateCategory";
import EventTemplateSummaryItem from "./models/eventTemplateSummaryItem";
import {orderBy} from "lodash";
import EventDetails from "./models/eventDetails";
import {EventCatalogueSortField} from "../../../shared/enums/eventCatalogueSortField";
import {parseDuration} from "../../../utils/dateUtils";
import {SortOrder} from "../../../shared/enums/sortOrder";
import {FileUri} from "../../../shared/interfaces/fileUri";
import {IUserService} from "../../../services/userService";
import {EventCalendarType} from "../../../shared/enums/eventCalendarType";

/**
 * The interface for EventCatalogueDomainStore.
 *
 * @interface
 */
export interface IEventCatalogueDomainStore {
  /**
   * An array of event template categories.
   */
  eventTemplateCategories: EventTemplateCategory[];
  /**
   * An array of event templates.
   */
  eventTemplates: EventTemplateSummaryItem[];
  /**
   * Load the event templates from the backend and set the eventTemplates property.
   * Has optional search term parameter for filtering the event templates
   */
  loadEventTemplates: (
    status: EventTemplateStatus,
    sortField: EventCatalogueSortField,
    searchTerm?: string,
    calendarType?: EventCalendarType,
    abortController?: AbortController
  ) => Promise<void>;
  /**
   * Load the event template categories from the backend and set the eventTemplateCategories property.
   * Has optional search term parameter for filtering the event templates
   */
  loadEventTemplateCategories: (
    status: EventTemplateStatus,
    maxTemplates: number,
    sortField: EventCatalogueSortField,
    searchTerm?: string,
    showDynamicCategories?: boolean,
    calendarType?: EventCalendarType,
    abortController?: AbortController
  ) => Promise<void>;
  /**
   * Set the status for an event template
   */
  setEventTemplateStatus: (
    id: string,
    status: EventTemplateStatus
  ) => Promise<void>;
  /**
   * Retrieve event template image.
   */
  getEventTemplateImage: (
    imageId: string,
    width?: number,
    height?: number,
    ttl?: number,
    abortController?: AbortController
  ) => Promise<string>;
  /**
   * Sort event template in the selected category.
   */
  sortEventTemplates: (
    categoryId: string,
    sortField: string,
    sortOrder: string
  ) => void;
  /**
   * Reset event template categories.
   */
  resetEventTemplateCategories: () => void;
  /**
   * Reset event templates.
   */
  resetEventTemplates: () => void;
  eventTemplate: EventTemplate;
  /**
   * The the event categories from the backend and set the eventTemplate.category.items.
   */
  loadEventCategories: (calendarType: EventCalendarType) => Promise<void>;
  createEventTemplate: () => Promise<string>;
  updateEventTemplate: () => Promise<string>;
  /**
   * Reset event template to a new instance.
   */
  resetEventTemplate: () => void;
  /**
   * The event template category that has been selected.
   */
  eventTemplateCategory: EventTemplateCategory;
  /**
   * Load the event template category from the backend and set the eventTemplateCategory property.
   */
  loadEventTemplateCategory: (
    categoryId: string,
    status: EventTemplateStatus,
    calendarType: EventCalendarType
  ) => Promise<void>;
  /**
   * Load an event template
   */
  loadEventTemplate: (id: string) => Promise<void>;
  /**
   * Load the image for the loaded event template
   */
  loadEventTemplateImage: () => Promise<void>;
  /**
   * Create an event from an event template.
   */
  createEventFromTemplate: (id: string) => Promise<EventDetails>;
  /**
   * Selected event template reference as "<category> | <title>"
   */
  selectedEventTemplateRef: string;

  sortCatalogueSearchResult: (sortField: string, sortOrder: SortOrder) => void;
}

/**
 * The store that manage the logic and state of event catalogue objects.
 *
 * @class
 * @implements {IEventCatalogueDomainStore}
 */
export default class EventCatalogueDomainStore
  implements IEventCatalogueDomainStore
{
  private readonly eventCatalogueService: IEventCatalogueService;
  private readonly eventService: IEventService;
  private readonly fileService: IFileService;
  private readonly userService: IUserService;
  private eventCategories: KeyValue[] = [];
  private templatesRequestId = 0;
  private categoriesRequestId = 0;

  eventTemplates: EventTemplateSummaryItem[] = [];
  eventTemplateCategories: EventTemplateCategory[] = [];
  eventTemplateCategory: EventTemplateCategory;
  eventTemplate: EventTemplate;
  selectedEventTemplateRef: string = "";
  searchTerm?: string;

  constructor(
    eventCatalogueService: IEventCatalogueService,
    fileService: IFileService,
    eventService: IEventService,
    userService: IUserService
  ) {
    makeObservable(this, {
      eventTemplates: observable,
      eventTemplateCategories: observable,
      eventTemplateCategory: observable,
      eventTemplate: observable,
      loadEventTemplates: action,
      loadEventTemplateCategories: action,
      loadEventTemplateCategory: action,
      resetEventTemplates: action,
      resetEventTemplateCategories: action
    });
    this.eventCatalogueService = eventCatalogueService;
    this.eventService = eventService;
    this.fileService = fileService;
    this.userService = userService;
    this.eventTemplate = new EventTemplate();
    this.eventTemplateCategory = new EventTemplateCategory("", "", 0, [], 1);
  }

  loadEventTemplateCategory = async (
    categoryId: string,
    status: EventTemplateStatus,
    calendarType: EventCalendarType
  ): Promise<void> => {
    const data = await this.eventCatalogueService.getEventTemplateCategory(
      categoryId,
      status,
      calendarType
    );

    runInAction(() => {
      this.eventTemplateCategory = new EventTemplateCategory(
        data.id,
        data.name,
        data.eventTemplatesCount,
        data.eventTemplates.map(
          (eventTemplate: any) =>
            new EventTemplateSummaryItem(
              eventTemplate.id,
              eventTemplate.title,
              eventTemplate.description,
              eventTemplate.lastEditedDate,
              status,
              eventTemplate.imageId,
              data.name,
              eventTemplate.isVillageFavourite,
              eventTemplate.isNew
            )
        ),
        data.categoryType
      );
    });
  };

  loadEventTemplates = async (
    status: EventTemplateStatus,
    sortField: EventCatalogueSortField,
    searchTerm?: string,
    calendarType?: EventCalendarType,
    abortController?: AbortController
  ): Promise<void> => {
    const requestId = ++this.templatesRequestId;
    const data = await this.eventCatalogueService.getEventTemplates(
      status,
      sortField,
      searchTerm,
      calendarType,
      abortController
    );

    if (!data || requestId !== this.templatesRequestId) {
      return;
    }
    runInAction(() => {
      this.eventTemplates = data.map(
        (template: any) =>
          new EventTemplateSummaryItem(
            template.id,
            template.title,
            template.description,
            new Date(template.lastEditedDate),
            status,
            template.imageId,
            template.category.name,
            template.isVillageFavourite,
            template.isNew
          )
      );
    });
  };

  loadEventTemplateCategories = async (
    status: EventTemplateStatus,
    maxTemplates: number,
    sortField: EventCatalogueSortField,
    searchTerm?: string,
    showDynamicCategories?: boolean,
    calendarType?: EventCalendarType,
    abortController?: AbortController
  ): Promise<void> => {
    const requestId = ++this.categoriesRequestId;
    this.searchTerm = searchTerm;
    const data = await this.eventCatalogueService.getEventTemplateCategories(
      status,
      maxTemplates,
      sortField,
      searchTerm,
      showDynamicCategories,
      calendarType,
      abortController
    );

    if (
      !data?.eventTemplateCategories ||
      requestId !== this.categoriesRequestId
    ) {
      return;
    }
    runInAction(() => {
      this.eventTemplateCategories = data.eventTemplateCategories.map(
        (category: any) =>
          new EventTemplateCategory(
            category.id,
            category.name,
            category.eventTemplatesCount,
            category.eventTemplates.map(
              (template: any) =>
                new EventTemplateSummaryItem(
                  template.id,
                  template.title,
                  template.description,
                  new Date(template.lastEditedDate),
                  status,
                  template.imageId,
                  category.name,
                  template.isVillageFavourite,
                  template.isNew
                )
            ),
            category.categoryType
          )
      );
    });
  };

  setEventTemplateStatus = async (
    id: string,
    status: EventTemplateStatus
  ): Promise<void> => {
    await this.eventCatalogueService.setEventTemplateStatus(id, status);
  };

  getEventTemplateImage = async (
    imageId: string,
    width: number = 50,
    height: number = 50,
    ttl: number = 30,
    abortController?: AbortController
  ): Promise<string> => {
    const key = `${imageId}_${width}_${height}`;
    const content = localStorage.getItem(key);
    if (content) {
      const fileUri: FileUri = JSON.parse(content);
      const isItemExpired =
        new Date(fileUri.expiryDate).getTime() < new Date().getTime();
      if (isItemExpired) {
        return this.getImageFromFileService(
          key,
          imageId,
          width,
          height,
          ttl,
          abortController
        );
      }
      return fileUri.uri || "";
    }
    return this.getImageFromFileService(
      key,
      imageId,
      width,
      height,
      ttl,
      abortController
    );
  };

  private getImageFromFileService = async (
    key: string,
    imageId: string,
    width: number = 50,
    height: number = 50,
    ttl: number = 30,
    abortController?: AbortController
  ): Promise<string> => {
    const fileUrisResponse = await this.fileService.getFileUris(
      [imageId],
      width,
      height,
      ttl,
      abortController
    );

    try {
      const fileUri = fileUrisResponse.fileUris[0];
      if (fileUri.uri) {
        localStorage.setItem(key, JSON.stringify(fileUri));
        return fileUri.uri;
      }
      return "";
    } catch (e: any) {
      return "";
    }
  };

  sortEventTemplates = (
    categoryId: string,
    sortField: string,
    sortOrder: string
  ) => {
    this.eventTemplateCategories.forEach((category) => {
      if (category.id === categoryId) {
        category.eventTemplates = orderBy(
          category.eventTemplates,
          [
            (template) => {
              switch (sortField) {
                case "title": {
                  return template.title.toLowerCase();
                }
                case "lastEditedDate": {
                  return template.lastEditedDate;
                }
              }
            }
          ],
          [sortOrder as "asc" | "desc"]
        );
      }
    });
  };

  resetEventTemplates = () => {
    this.eventTemplates = [];
  };

  resetEventTemplateCategories = () => {
    this.eventTemplateCategories = [];
  };

  loadEventCategories = async (
    calendarType: EventCalendarType
  ): Promise<void> => {
    const data = await this.eventService.getAllEventCategories(calendarType);

    this.eventCategories = data
      .sort((a: any, b: any) => a.name.localeCompare(b.name))
      .map((d: any) => new KeyValue(d.id, d.name));

    runInAction(() =>
      this.eventTemplate.category.setItems(this.eventCategories)
    );
  };

  createEventTemplate = async (): Promise<string> => {
    await this.checkForNewImageAndUpload();
    return this.eventCatalogueService
      .createEventTemplate(this.eventTemplate)
      .then((response: ResourceCreatedOrUpdated) => {
        this.resetEventTemplate();
        return response?.title;
      });
  };

  updateEventTemplate = async (): Promise<string> => {
    await this.checkForNewImageAndUpload();
    return this.eventCatalogueService
      .updateEventTemplate(this.eventTemplate)
      .then((response: ResourceCreatedOrUpdated) => response?.id);
  };

  resetEventTemplate = () => {
    runInAction(() => {
      this.eventTemplate = new EventTemplate();
    });
  };

  private checkForNewImageAndUpload = async () => {
    if (
      this.eventTemplate.image.hasNewUpload &&
      this.eventTemplate.image.file !== null
    ) {
      await this.fileService
        .uploadFile(
          this.eventTemplate.image.file,
          this.eventTemplate.image.fileName
        )
        .then((response: FileCreated) => {
          this.eventTemplate.image.setFileId(response?.fileId);
        });
    }
  };

  loadEventTemplate = async (id: string): Promise<void> => {
    const data = await this.eventCatalogueService.getEventTemplateById(id);
    const eventDuration = parseDuration(data.duration);
    runInAction(() => {
      this.eventTemplate = new EventTemplate(
        data.id,
        data.title,
        data.description,
        data.isVillageFavourite,
        data.category.id,
        eventDuration.hours! + eventDuration.minutes! / 60,
        data.imageId,
        data.calendarType
      );
    });
  };

  loadEventTemplateImage = async () => {
    const fileId = this.eventTemplate?.image?.fileId;
    if (fileId) {
      var file = await this.fileService.getFile(fileId);
      this.eventTemplate.image.setImageFromDownload(file);
    }
  };

  createEventFromTemplate = async (id: string): Promise<EventDetails> => {
    const template = await this.eventCatalogueService.getEventTemplateById(id);
    this.selectedEventTemplateRef = `${template.category.name} | ${template.title}`;

    const duration = parseDuration(template.duration);

    let hourNumber = duration.hours ?? 0;
    let minutesNumber = duration.minutes ?? 0;

    return new EventDetails(
      this.userService.getSelectedVillage()?.timeZone!,
      undefined,
      template.title,
      template.category.id,
      undefined,
      undefined,
      template.description,
      undefined,
      undefined,
      false,
      {amount: 0},
      undefined,
      undefined,
      template.imageId,
      undefined,
      undefined,
      template.id,
      hourNumber,
      minutesNumber
    );
  };

  sortCatalogueSearchResult = (sortField: string, sortOrder: SortOrder) => {
    this.eventTemplates = orderBy(
      this.eventTemplates,
      [
        (template) => {
          switch (sortField) {
            case "title": {
              return template.title.toLowerCase();
            }
            case "lastEditedDate": {
              return template.lastEditedDate;
            }
          }
        }
      ],
      [sortOrder]
    );
  };
}
